import React from 'react';
import { useSelector } from 'react-redux';
import {
  Ad,
  AppContent,
  ContentAndSidebar,
  ErrorBoundary,
  ExpandableHC,
  LARGE_SCREEN_SIZE,
  MEDIA_BREAKPOINTS,
  RecommendationsAdvertisement,
  ScrollableArticleList,
  SectionWithTitle,
  SPORTRADAR_NAMES,
  SportradarFactory,
} from 'mm-ui-components';
import { Store } from 'redux';
import { css, StyleSheet } from 'aphrodite/no-important';
import { Theme } from '../../components/connectedComponents/Theme';
import { MetaTags } from '../../components/connectedComponents/MetaTags';
import { HeadScripts } from '../../components/connectedComponents/HeadScripts';
import { SECTION_NAMES, SLOT_NAMES } from '../templates.const';
import { Page } from '../Page';
import {
  getAdSlotId,
  getAdSlotOnSuccess,
  getCanonicalUrl,
  getExperiments,
  getUseFormatMinutesHoursAgo,
} from '../../store/config/config.selectors';
import { ErrorReporter } from '../../errorReporting/errorReporter.type';
import { AppActions, AppState } from '../../store/initialStateRegistration';
import { useMeasureHydrateTime } from '../useMeasureHydrateTime';
import {
  getMetadataDescription,
  getMetadataTitle,
  getSectionArticlesByKey,
  getMetadataImage,
  getHreflangData,
  getShowRecommendationsAdvertisement,
  getOutbrainWidgetID,
  getSportradarProps,
} from '../../store/template/feedPage/feedPageManchester/feedPageManchester.selectors';
import { NavigationLayout } from '../../components/connectedComponents/NavigationLayout';
import {
  AdditionalStyleSheet,
  getPageMetaText,
} from '../templates.utils';
import { createSIMetaImage } from '../../store/template/template.utils';

interface FeedPageBangkokProps {
  store: Store<AppState, AppActions>;
  errorProvider: ErrorReporter;
}

const getArticlesInTemplate = (state: AppState) => {
  const topSectionArticles = getSectionArticlesByKey(SECTION_NAMES.TOP_SECTION)(state) || [];
  const feedSection1Articles = getSectionArticlesByKey(SECTION_NAMES.SECTION1)(state) || [];
  const feedSection2Articles = getSectionArticlesByKey(SECTION_NAMES.SECTION2)(state) || [];

  return [
    ...topSectionArticles,
    ...feedSection1Articles,
    ...feedSection2Articles,
  ];
};

export const FeedPageManchesterComponent: React.FunctionComponent<
  FeedPageBangkokProps
> = ({ store, errorProvider }) => {
  useMeasureHydrateTime();
  return (
    <Page store={store} errorProvider={errorProvider}>
      <ErrorBoundary>
        <FeedPageManchesterContentConnected />
      </ErrorBoundary>
    </Page>
  );
};

const createStyle = () => StyleSheet.create({
    contentContainer: {
      margin: '0 auto',
      maxWidth: LARGE_SCREEN_SIZE,
      padding: '0',
      [MEDIA_BREAKPOINTS.medium]: {
        padding: '0 20px',
      },
      [MEDIA_BREAKPOINTS.small]: {
        padding: '0 20px',
      },
    },
    navContainer: {
      margin: '0 0 20px',
      backgroundColor: 'white',
      boxShadow: '0 4px 4px -2px #ddd',
    },
    navSubContainer: {
      display: 'flex',
      alignItems: 'center',
      maxWidth: LARGE_SCREEN_SIZE,
      margin: '0 auto',
      padding: '0',
      justifyContent: 'flex-end',
    },
    noStyles: {
      boxShadow: 'none',
      margin: '0',
      padding: '0',
    },
    matchHeader: {
      padding: '0 0 10px',
      margin: '10px 0 20px',
    },
    padding: {
      padding: '20px 0',
    },
    header: {
      flex: '1 1 auto',
      [MEDIA_BREAKPOINTS.medium]: {
        padding: '20px 30px',
      },
      [MEDIA_BREAKPOINTS.small]: {
        padding: '20px',
      },
    },
    displayNone: {
      [MEDIA_BREAKPOINTS.medium]: {
        display: 'none',
      },
      [MEDIA_BREAKPOINTS.small]: {
        display: 'none',
      },
    },
    imageContainer: {
      display: 'flex',
      overflow: 'hidden',
      height: '135px',
      opacity: 0.3,
      alignItems: 'center',
      flex: '0 0 auto',
      [MEDIA_BREAKPOINTS.small]: {
        display: 'none',
      },
    },
  });

const sectionContainer = (styles?: any) => StyleSheet.create({
    style: {
      backgroundColor: 'white',
      boxShadow: '0 0 3px rgba(0,0,0,.2)',
      marginBottom: '20px',
      padding: '20px 30px',
      ...styles,
    },
  }) as AdditionalStyleSheet;

const gridContainer = StyleSheet.create({
  style: {
    [MEDIA_BREAKPOINTS.large]: {
      gridTemplateColumns: '320px 1fr',
      columnGap: '20px',
    },
    [MEDIA_BREAKPOINTS.medium]: {
      width: '100%',
    },
    [MEDIA_BREAKPOINTS.small]: {
      width: '100%',
    },
  },
}) as AdditionalStyleSheet;

export const FeedPageManchesterContentConnected: React.FunctionComponent = () => {
    const topSectionArticles = useSelector(
      getSectionArticlesByKey(SECTION_NAMES.TOP_SECTION),
    );
    const section1Articles = useSelector(
      getSectionArticlesByKey(SECTION_NAMES.SECTION1),
    );
    const section2Articles = useSelector(
      getSectionArticlesByKey(SECTION_NAMES.SECTION2),
    );
    // TODO: Will add logo when there is a usable logo url
    // const topImage = useSelector(
    //   getSectionImageByKey(SECTION_NAMES.TOP_SECTION),
    // );
    const sportradarProps = useSelector(getSportradarProps());
    const canonicalURL = useSelector(getCanonicalUrl);
    const showRecommendationsAdvertisement = useSelector(
      getShowRecommendationsAdvertisement,
    );
    const outbrainWidgetID = useSelector(getOutbrainWidgetID);
    const createSportradarName = (sportradarName: string) => {
      const formatLeague = `${sportradarProps?.league?.toUpperCase()}${sportradarName}`;
      // @ts-ignore
      return formatLeague.length > sportradarName.length ? SPORTRADAR_NAMES[formatLeague] : null;
    };

    const useFormatMinutesHoursAgo = useSelector(getUseFormatMinutesHoursAgo);

    const articles = useSelector(getArticlesInTemplate);
    const experiments = useSelector(getExperiments);
    const topAdId = useSelector(getAdSlotId(SLOT_NAMES.TOP_AD));
    const sidebar1Ad = useSelector(getAdSlotId(SLOT_NAMES.SIDE_BAR_1));
    const sidebar2Ad = useSelector(getAdSlotId(SLOT_NAMES.SIDE_BAR_2));
    const sideBar1AdOnSuccess = useSelector(
      getAdSlotOnSuccess(SLOT_NAMES.SIDE_BAR_1),
    );
    const sideBar2AdOnSuccess = useSelector(
      getAdSlotOnSuccess(SLOT_NAMES.SIDE_BAR_2),
    );
    const topAdOnSuccess = useSelector(getAdSlotOnSuccess(SLOT_NAMES.TOP_AD));
    const oneOnOneId = useSelector(getAdSlotId(SLOT_NAMES.ONE_ON_ONE));
    const oneOnOneOnSuccess = useSelector(
      getAdSlotOnSuccess(SLOT_NAMES.ONE_ON_ONE),
    );
    const metaTitle = useSelector(getMetadataTitle);
    const metaDescription = useSelector(getMetadataDescription);
    let pageImage = useSelector(getMetadataImage);
    pageImage = createSIMetaImage(canonicalURL, pageImage);
    const hreflangData = useSelector(getHreflangData) || [];
    const style = createStyle();

    return (
      <React.Fragment>
        <MetaTags
          {...getPageMetaText(metaTitle, metaDescription)}
          articles={articles}
          pageImage={pageImage}
          alternateUrls={hreflangData}
        />
        <HeadScripts />
        <Theme>
          <AppContent>
            <NavigationLayout
              mainWrapperConfigThemeKey="mainContent"
              isFullBleed
              noMarginTop
            >
              <div className={css(style.navContainer)}>
                <div className={css(style.navSubContainer)}>
                  <SportradarFactory
                    sportradarProps={sportradarProps}
                    name={createSportradarName('_TEAM_HEADER')}
                    wrapperStyle={[style.noStyles, style.padding, style.header]}
                    widgetStyle={style.noStyles}
                  />
                  {/* <div className={css(style.imageContainer)}>
                    <ArticleImage
                      image={{ ...topImage, aspectRatio: { x: 1, y: 1 } }}
                      sizeType={REGULAR_IMAGE_SIZE_TYPE}
                      defaultBlocksWidths={createBlocksWidth(240, 240, 240)}
                    />
                  </div> */}
                </div>
              </div>
              <Ad id={topAdId} onSuccess={topAdOnSuccess} setMinHeight />
              <div className={css(style.contentContainer)}>
                {sportradarProps.matchId ? (
                  <SportradarFactory
                    name={SPORTRADAR_NAMES.TEAM_MATCH_SMALL}
                    sportradarProps={sportradarProps}
                    wrapperStyle={style.matchHeader}
                    widgetStyle={style.noStyles}
                  />
                ) : null}
                <SectionWithTitle
                  additionalStyle={sectionContainer({ margin: '20px 0', boxShadow: 'none', padding: '0' })}
                >
                  <ScrollableArticleList
                    cardsProps={topSectionArticles}
                    paddingFactors={{ large: 0.5, medium: 0.5, small: 0.5 }}
                    useFormatMinutesHoursAgo={useFormatMinutesHoursAgo}
                  />
                </SectionWithTitle>
                <ContentAndSidebar additionalStyles={gridContainer}>
                  <div>
                    {sportradarProps?.league !== 'ncaaf' ? (
                      <SportradarFactory
                        name={createSportradarName('_RECENT_GAMES')}
                        sportradarProps={sportradarProps}
                        buttonTitle="Full Schedule"
                        buttonUrl={`${canonicalURL}/schedule`}
                      />
                    ) : (
                      <SportradarFactory
                        name={createSportradarName('_SCHEDULE')}
                        sportradarProps={sportradarProps}
                        buttonTitle="Full Schedule"
                        buttonUrl={`${canonicalURL}/schedule`}
                      />
                    )}
                    <SportradarFactory
                      name={createSportradarName('_STANDINGS_SLIM')}
                      sportradarProps={sportradarProps}
                      buttonTitle="Full Standings"
                      buttonUrl={`https://www.si.com/${sportradarProps.league}/standings`}
                    />
                    <SportradarFactory
                      name={createSportradarName('_TEAM_LEADERS')}
                      sportradarProps={sportradarProps}
                      wrapperStyle={style.displayNone}
                      buttonTitle="Full Team Statistics"
                      buttonUrl={`${canonicalURL}/stats`}
                    />
                    <SectionWithTitle additionalStyle={sectionContainer()}>
                      <Ad id={sidebar1Ad} onSuccess={sideBar1AdOnSuccess} />
                    </SectionWithTitle>
                    <SportradarFactory
                      name={createSportradarName('_INJURIES')}
                      wrapperStyle={style.displayNone}
                      sportradarProps={{
                        ...sportradarProps,
                        disableHeader: true,
                        disableTeamHeaders: true,
                      }}
                    />
                    <SportradarFactory
                      wrapperStyle={style.displayNone}
                      name={createSportradarName('_RANKS')}
                      sportradarProps={{
                        ...sportradarProps,
                        ...(sportradarProps?.league === 'mlb' && { category: 'hitting' }),
                      }}
                    />
                    {sportradarProps?.league === 'mlb' ? (
                      <SportradarFactory
                        wrapperStyle={style.displayNone}
                        name={createSportradarName('_RANKS')}
                        sportradarProps={{
                          ...sportradarProps,
                          category: 'pitching',
                        }}
                      />
                    ) : null}
                  </div>
                  <div>
                    <SectionWithTitle
                      additionalStyle={sectionContainer({
                        padding: '20px 10px',
                      })}
                    >
                      <ExpandableHC
                        cards={section1Articles}
                        showImageHeightByAspectRatio
                        paddingFactors={{ large: 0.5, medium: 0.5, small: 0.5 }}
                        useFormatMinutesHoursAgo={useFormatMinutesHoursAgo}
                      />
                    </SectionWithTitle>
                    <Ad id={sidebar2Ad} onSuccess={sideBar2AdOnSuccess} setMinHeight />
                    {/* <SectionWithTitle additionalStyle={sectionContainer()}>
                      // <Ad id={sidebar2Ad} onSuccess={sideBar2AdOnSuccess} />
                    </SectionWithTitle> */}
                    <SectionWithTitle
                      additionalStyle={sectionContainer({
                        padding: '20px 10px',
                      })}
                    >
                      <ExpandableHC
                        cards={section2Articles}
                        showImageHeightByAspectRatio
                        paddingFactors={{ large: 0.5, medium: 0.5, small: 0.5 }}
                      />
                    </SectionWithTitle>
                  </div>
                </ContentAndSidebar>
                {showRecommendationsAdvertisement && !!outbrainWidgetID ? (
                  <RecommendationsAdvertisement
                    experiment={experiments?.join()}
                    outbrainWidgetID={outbrainWidgetID}
                    pageLiveUrl={canonicalURL}
                  />
                ) : null}
              </div>
              <Ad id={oneOnOneId} onSuccess={oneOnOneOnSuccess} setMinHeight />
            </NavigationLayout>
          </AppContent>
        </Theme>
      </React.Fragment>
    );
  };
